import { useRef, useState } from 'react';
import { Camera, Loader2 } from 'lucide-react';

import { useAuthStore } from '@/stores/authStore';
import { uploadAvatar, removeAvatar } from '@/lib/api/auth';
import { Avatar } from './Avatar';
import { ProfileSettingsForm } from './ProfileSettingsForm';
import { ResumeUpload } from './ResumeUpload';
import { SecuritySettingsForm } from './SecuritySettingsForm';
import { cn } from '@/lib/cn';

type Section = 'profile' | 'security';

const ACCEPTED = 'image/png,image/jpeg,image/webp';
const MAX_BYTES = 4 * 1024 * 1024;

const NAV: { id: Section; label: string }[] = [
  { id: 'profile', label: 'Profile' },
  { id: 'security', label: 'Security' },
];

function formatRole(role: string): string {
  if (role === 'super_admin') return 'Super Admin';
  if (role === 'org_admin') return 'Org Admin';
  return role.charAt(0).toUpperCase() + role.slice(1);
}

export function SettingsLayout() {
  const { user, setAuth } = useAuthStore();
  const [section, setSection] = useState<Section>('profile');
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  if (!user) return null;

  const hasUploadedAvatar = !!user.avatarUrl?.startsWith('/');

  const handleAvatarPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setAvatarError(null);
    if (!ACCEPTED.split(',').includes(file.type)) {
      setAvatarError('PNG, JPEG, or WebP only.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setAvatarError('Image must be at most 4 MB.');
      return;
    }
    setAvatarBusy(true);
    try {
      const res = await uploadAvatar(file);
      setAuth(res.access_token);
    } catch {
      setAvatarError('Upload failed. Try again.');
    } finally {
      setAvatarBusy(false);
    }
  };

  const handleRemove = async () => {
    setAvatarError(null);
    setAvatarBusy(true);
    try {
      const res = await removeAvatar();
      setAuth(res.access_token);
    } catch {
      setAvatarError('Remove failed. Try again.');
    } finally {
      setAvatarBusy(false);
    }
  };

  return (
    <div className="px-4 py-6 sm:py-8 sm:px-8 max-w-5xl mx-auto w-full">
      <div className="mb-5 sm:mb-6">
        <h1
          className="text-[1.375rem] font-bold tracking-tight text-foreground"
          style={{ fontFamily: 'DM Sans, system-ui, sans-serif' }}
        >
          Settings
        </h1>
        <p className="mt-1 text-[13px] text-muted-foreground">
          Manage your profile and security preferences
        </p>
      </div>

      {/* ── Mobile: identity strip + tab strip ── */}
      <div className="sm:hidden mb-4">
        <div className="rounded-2xl border border-border bg-white shadow-sm overflow-hidden">
          {/* Identity row */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
            <div
              className="relative group cursor-pointer flex-shrink-0"
              onClick={() => !avatarBusy && fileRef.current?.click()}
            >
              <Avatar src={user.avatarUrl} name={user.username} size={36} rounded="lg" />
              <div className="absolute inset-0 rounded-lg bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                {avatarBusy ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-white" />
                ) : (
                  <Camera className="h-3.5 w-3.5 text-white" />
                )}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <p className="text-[13px] font-bold text-foreground truncate">{user.username}</p>
                <span className="flex-shrink-0 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold capitalize text-foreground/70">
                  {formatRole(user.role ?? '')}
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground truncate">{user.email}</p>
            </div>
          </div>
          {avatarError && (
            <p className="px-4 py-1.5 text-[11px] text-destructive bg-destructive/5 border-b border-border">
              {avatarError}
            </p>
          )}
          {/* Tab strip */}
          <div className="flex">
            {NAV.map(({ id, label }) => (
              <button
                key={id}
                onClick={() => setSection(id)}
                className={cn(
                  'flex-1 py-2.5 text-[13px] font-medium transition-colors border-b-2',
                  section === id
                    ? 'border-foreground text-foreground'
                    : 'border-transparent text-muted-foreground',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-start gap-5">
        {/* ── Desktop: left sidebar ── */}
        <div className="hidden sm:flex w-[192px] flex-shrink-0 flex-col rounded-2xl border border-border bg-white shadow-sm overflow-hidden">
          <div className="flex flex-col items-center px-4 pt-6 pb-3 gap-2">
            {/* Avatar — click to upload */}
            <div
              className="relative group cursor-pointer"
              onClick={() => !avatarBusy && fileRef.current?.click()}
            >
              <Avatar src={user.avatarUrl} name={user.username} size={72} rounded="2xl" />
              <div className="absolute inset-0 rounded-2xl bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1 pointer-events-none">
                {avatarBusy ? (
                  <Loader2 className="h-5 w-5 animate-spin text-white" />
                ) : (
                  <>
                    <Camera className="h-5 w-5 text-white" />
                    <span className="text-[10px] font-semibold text-white tracking-wide">
                      Change
                    </span>
                  </>
                )}
              </div>
            </div>

            {avatarError && (
              <p className="text-[10.5px] text-destructive text-center leading-snug">
                {avatarError}
              </p>
            )}
            {hasUploadedAvatar && !avatarBusy && (
              <button
                onClick={handleRemove}
                className="text-[10.5px] text-muted-foreground/50 hover:text-destructive transition-colors"
              >
                Remove photo
              </button>
            )}

            {/* Identity */}
            <div className="mt-1 w-full min-w-0 text-center">
              <p className="text-[13px] font-bold text-foreground truncate">{user.username}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{user.email}</p>
              <span className="mt-2 inline-block rounded-full bg-secondary px-2.5 py-0.5 text-[10.5px] font-semibold capitalize text-foreground/70">
                {formatRole(user.role ?? '')}
              </span>
            </div>
          </div>

          {/* Nav */}
          <div className="border-t border-border px-2 py-2">
            {NAV.map(({ id, label }) => (
              <button
                key={id}
                onClick={() => setSection(id)}
                className={cn(
                  'w-full rounded-lg px-3 py-2 text-left text-[13px] font-medium transition-colors',
                  section === id
                    ? 'bg-secondary text-foreground'
                    : 'text-muted-foreground hover:bg-secondary/60 hover:text-foreground',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Content panel ── */}
        <div className="flex-1 min-w-0 space-y-5">
          {section === 'profile' && <ProfileSettingsForm />}
          {section === 'profile' && user.role === 'student' && <ResumeUpload />}
          {section === 'security' && <SecuritySettingsForm />}
        </div>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept={ACCEPTED}
        onChange={handleAvatarPick}
        className="hidden"
      />
    </div>
  );
}
